import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : 'https://mnemonic-production.up.railway.app/api/v1';

const AuthContext = createContext(null);

// Exported so non-hook contexts (e.g. APIKeysView) can use it after getting it from useAuth()
export function makeApiFetch(navigate) {
  return async function apiFetch(path, options = {}) {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
    if (res.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('api_key');
      localStorage.removeItem('user_email');
      navigate('/login');
      throw new Error('Session expired. Please log in again.');
    }
    return res;
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  const [apiKey, setApiKey] = useState(localStorage.getItem('api_key'));
  const navigate = useNavigate();

  // Check if user is logged in on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedApiKey = localStorage.getItem('api_key');
    const storedEmail = localStorage.getItem('user_email');
    
    if (storedToken) {
      setToken(storedToken);
      setApiKey(storedApiKey);
      setUser({ email: storedEmail || 'user' });
    }
    
    setLoading(false);
  }, []);

  const signup = async (email, password, fullName) => {
    try {
      const response = await fetch(`${API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          full_name: fullName
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Signup failed';
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } else {
            errorMessage = `Server error (${response.status})`;
          }
        } catch (parseError) {
          if (response.status === 400) {
            errorMessage = 'Invalid signup data or email already exists';
          } else if (response.status === 404) {
            errorMessage = 'Signup endpoint not found. Please check your configuration.';
          } else {
            errorMessage = `Signup failed (${response.status})`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store tokens and email
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('api_key', data.api_key);
      localStorage.setItem('user_email', email);
      
      setToken(data.access_token);
      setApiKey(data.api_key);
      setUser({ email });

      return data;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } else {
            // Non-JSON response (like 404 HTML)
            errorMessage = `Server error (${response.status})`;
          }
        } catch (parseError) {
          // If JSON parsing fails, use status-based message
          if (response.status === 401) {
            errorMessage = 'Invalid email or password';
          } else if (response.status === 404) {
            errorMessage = 'Login endpoint not found. Please check your configuration.';
          } else {
            errorMessage = `Login failed (${response.status})`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Store tokens and email
      localStorage.setItem('auth_token', data.access_token);
      localStorage.setItem('user_email', email);
      if (data.api_key) {
        localStorage.setItem('api_key', data.api_key);
      }
      
      setToken(data.access_token);
      if (data.api_key) {
        setApiKey(data.api_key);
      }
      setUser({ email });

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('api_key');
    localStorage.removeItem('user_email');
    setToken(null);
    setApiKey(null);
    setUser(null);
    navigate('/login');
  };

  const apiFetch = useCallback(makeApiFetch(navigate), [navigate]);

  const value = {
    user,
    token,
    apiKey,
    loading,
    signup,
    login,
    logout,
    apiFetch,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
